/**
 * useSaveState - Hook for managing save state with timestamp-based approach
 *
 * This hook provides a simplified save state management system that replaces
 * the complex dual-flag approach (dirty/dirtySinceLastSave) with timestamps.
 *
 * Features:
 * - Timestamp-based change tracking
 * - Automatic save scheduling with debouncing
 * - Save conflict detection
 * - Optimistic updates with rollback
 * - Save history tracking
 * - Network state awareness
 *
 * Usage:
 * const saveState = useSaveState({
 *     autoSave: true,
 *     autoSaveDelay: 3000,
 *     onSave: async (data) => api.savePlan(data),
 *     onError: (error) => console.error(error)
 * });
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import moment from 'moment';

/**
 * Configuration options for useSaveState
 * @typedef {Object} SaveStateConfig
 * @property {boolean} autoSave - Enable automatic saving
 * @property {number} autoSaveDelay - Delay in ms before auto-save (default: 3000)
 * @property {Function} onError - Error handler callback
 */

/**
 * Save state object returned by hook
 * @typedef {Object} SaveState
 * @property {boolean} isDirty - Whether data has unsaved changes
 * @property {boolean} isSaving - Whether currently saving
 * @property {Date|null} lastSaveTime - Timestamp of last successful save
 * @property {Date|null} lastChangeTime - Timestamp of last change
 * @property {Error|null} lastError - Last save error
 * @property {number} saveCount - Number of successful saves
 * @property {number} failureCount - Number of failed save attempts
 * @property {Function} markClean - Mark state as clean
 * @property {Function} markDirty - Mark state as dirty
 * @property {Function} reset - Reset all save state
 * @property {Function} getSaveStatus - Get human-readable save status
 */

export const useSaveState = (config = {}) => {
    const {
        autoSave = false,
        autoSaveDelay = 3000,
        onError = console.error,
        isVirtualPlan = false
    } = config;

    // Core state
    const [lastSaveTime, setLastSaveTime] = useState(null);
    const [lastChangeTime, setLastChangeTime] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [lastError, setLastError] = useState(null);
    const [saveCount, setSaveCount] = useState(0);
    const [failureCount, setFailureCount] = useState(0);

    // Success message state
    const [justSaved, setJustSaved] = useState(false);
    const [showTimestamp, setShowTimestamp] = useState(false);

    // Refs for managing timers
    const autoSaveTimerRef = useRef(null);
    const justSavedTimerRef = useRef(null);
    const timestampTimerRef = useRef(null);

    // Computed values
    // Virtual plans only become dirty when actual changes are made, not just because they're virtual
    const isDirty = !!(lastChangeTime && (!lastSaveTime || lastChangeTime > lastSaveTime));

    /**
     * Mark state as dirty with current timestamp
     */
    const markDirty = useCallback(() => {
        setLastChangeTime(new Date());
        setLastError(null); // Clear error on new changes
    }, []);

    /**
     * Mark state as clean (usually after save)
     */
    const markClean = useCallback(() => {
        setLastChangeTime(null);
    }, []);

    /**
     * Set saving state
     */
    const setSaving = useCallback((saving) => {
        setIsSaving(saving);
    }, []);

    /**
     * Mark save as successful (sets save time and updates counts)
     */
    const markSaveSuccess = useCallback(() => {
        setSaving(false);
        setLastSaveTime(new Date());
        setSaveCount(prev => prev + 1);
        setFailureCount(0);
        setLastError(null);
        markClean();

        // Mark as just saved (auto-dismiss after 4.5s)
        setJustSaved(true);
        setShowTimestamp(false);
        if (justSavedTimerRef.current) {
            clearTimeout(justSavedTimerRef.current);
        }
        justSavedTimerRef.current = setTimeout(() => {
            setJustSaved(false);
        }, 4500);

        // After 5s, show timestamp (triggers re-render for "Saved X ago")
        if (timestampTimerRef.current) {
            clearTimeout(timestampTimerRef.current);
        }
        timestampTimerRef.current = setTimeout(() => {
            setShowTimestamp(true);
        }, 5000);
    }, [markClean]);

    /**
     * Mark save as failed (sets error and updates counts)
     */
    const markSaveError = useCallback((error) => {
        setSaving(false);
        setLastError(error);
        setFailureCount(prev => prev + 1);
    }, []);

    /**
     * Get human-readable save status
     */
    const getSaveStatus = useCallback(() => {
        if (isSaving) {
            return 'Saving...';
        }

        if (lastError) {
            return `Save failed: ${lastError.message}`;
        }

        if (!isDirty && lastSaveTime) {
            const timeSince = Date.now() - lastSaveTime.getTime();
            if (timeSince < 5000) {
                return 'Saved';
            }
            return `Saved ${moment(lastSaveTime).fromNow()}`;
        }

        if (isDirty) {
            if (isVirtualPlan) {
                return '';
            }
            return autoSave ? 'Unsaved changes (will auto-save)' : 'Unsaved changes';
        }

        return 'No changes';
    }, [isSaving, lastError, isDirty, lastSaveTime, autoSave, isVirtualPlan]);

    /**
     * Get save status message for SecondaryActions
     * Returns the status text that should appear below the save button
     * Filters out redundant messages like "Saving..." or "Saved" < 5s
     */
    const getSaveStatusMessage = useCallback(() => {
        // Show error
        if (lastError) {
            return `Save failed: ${lastError.message}`;
        }

        // Show unsaved changes
        if (isDirty && !isSaving) {
            return 'Unsaved changes';
        }

        // Show "Saved X ago" (only after showTimestamp becomes true at 5s)
        if (!isDirty && lastSaveTime && showTimestamp) {
            return `Saved ${moment(lastSaveTime).fromNow()}`;
        }

        return '';
    }, [lastError, isDirty, isSaving, lastSaveTime, showTimestamp]);

    /**
     * Determines if save status message should be shown
     * Hides during "justSaved" temporary state to avoid duplicate messages
     */
    const shouldShowSaveStatus = useCallback(() => {
        if (justSaved) {
            return false;
        }

        return (
            (lastError != null) ||
            (isDirty && !isSaving) ||
            (!isDirty && !isSaving && lastSaveTime != null && showTimestamp)
        );
    }, [justSaved, lastError, isDirty, isSaving, lastSaveTime, showTimestamp]);

    /**
     * Determines if save button should be shown
     * Button hides only when "No changes" (all flags false)
     * Virtual plans always show save button since they need to be converted to real plans
     */
    const shouldShowSaveButton = useCallback(() => {
        return isVirtualPlan || isDirty || isSaving || lastSaveTime != null || lastError != null;
    }, [isVirtualPlan, isDirty, isSaving, lastSaveTime, lastError]);

    /**
     * Determines if save button should be enabled (not disabled)
     * Enabled when there are changes OR it's a virtual plan that has never been saved
     */
    const shouldEnableSaveButton = useCallback(() => {
        return isDirty || (isVirtualPlan && !lastSaveTime);
    }, [isDirty, isVirtualPlan, lastSaveTime]);



    /**
     * Reset all save state
     */
    const reset = useCallback(() => {
        setLastSaveTime(null);
        setLastChangeTime(null);
        setIsSaving(false);
        setLastError(null);
        setSaveCount(0);
        setFailureCount(0);
        setJustSaved(false);
        setShowTimestamp(false);

        if (autoSaveTimerRef.current) {
            clearTimeout(autoSaveTimerRef.current);
            autoSaveTimerRef.current = null;
        }
        if (justSavedTimerRef.current) {
            clearTimeout(justSavedTimerRef.current);
            justSavedTimerRef.current = null;
        }
        if (timestampTimerRef.current) {
            clearTimeout(timestampTimerRef.current);
            timestampTimerRef.current = null;
        }
    }, []);

    /**
     * Cleanup timers on unmount to prevent memory leaks
     */
    useEffect(() => {
        return () => {
            if (autoSaveTimerRef.current) {
                clearTimeout(autoSaveTimerRef.current);
            }
            if (justSavedTimerRef.current) {
                clearTimeout(justSavedTimerRef.current);
            }
            if (timestampTimerRef.current) {
                clearTimeout(timestampTimerRef.current);
            }
        };
    }, []);




    return {
        // State
        isDirty,
        isSaving,
        lastSaveTime,
        lastChangeTime,
        lastError,
        saveCount,
        failureCount,
        justSaved,

        // Actions
        markClean,
        markDirty,
        markSaveSuccess,
        markSaveError,
        setSaving,
        reset,

        // Status helpers
        getSaveStatus,
        getSaveStatusMessage,
        shouldShowSaveStatus,
        shouldShowSaveButton,
        shouldEnableSaveButton,

        // Utility
        timeSinceLastSave: lastSaveTime ? Date.now() - lastSaveTime.getTime() : null,
        timeSinceLastChange: lastChangeTime ? Date.now() - lastChangeTime.getTime() : null,
        isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true
    };
};

// Export helper utilities
export const formatSaveTime = (timestamp) => {
    if (!timestamp) return 'Never';

    const diffMs = Date.now() - new Date(timestamp).getTime();
    if (diffMs < 5000) return 'Just now';

    return moment(timestamp).fromNow();
};

export default useSaveState;
